import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { ModuleEntry } from '@/config/moduleEntries';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { SsoAuthButtons } from '@/core/auth/components/SsoAuthButtons';
import { resolveSsoErrorMessage, notifySsoError, ssoErrorToastGuardKey } from '@/utils/ssoErrorMessage';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { STORAGE_KEYS } from '@/constants/storage';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { clearAuth, login, verifyMfaLogin } from '@/store/authSlice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { showLoginSuccessToast } from '@/utils/authTransition';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';

type FormData = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export interface LoginFormProps {
  moduleEntry?: ModuleEntry;
  onLoginSuccess: (result: {
    masterAdmin: boolean;
    permissions?: string[];
    enabledModules?: string[];
    accessibleModules?: string[];
    entryModule?: string | null;
    roleCode?: string | null;
    forcePasswordChange?: boolean;
  }) => void;
  showRegisterLink?: boolean;
  variant?: 'organization' | 'master';
}

export function LoginForm({
  moduleEntry,
  onLoginSuccess,
  showRegisterLink = true,
  variant = 'organization',
}: LoginFormProps) {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const schema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('validation.email.invalid', { ns: 'validation', defaultValue: 'Enter a valid work email' })),
        password: z.string().min(1, t('validation.password.required', { ns: 'validation', defaultValue: 'Password is required' })),
        rememberMe: z.boolean().optional(),
      }),
    [t],
  );
  const loading = useAppSelector((s) => s.auth.loading);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoError = searchParams.get('ssoError');
  const ssoDetail = searchParams.get('ssoDetail');
  const entryModule = moduleEntry?.erpModule ?? moduleEntry?.slug;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { rememberMe: true } });

  const ssoToastHandledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ssoError) return;
    const fingerprint = `${ssoError}|${ssoDetail ?? ''}`;
    if (ssoToastHandledRef.current === fingerprint) return;
    ssoToastHandledRef.current = fingerprint;
    const message = resolveSsoErrorMessage(ssoError, ssoDetail, 'login');
    if (message) {
      notifySsoError(message, ssoErrorToastGuardKey('login', ssoError, ssoDetail));
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('ssoError');
        next.delete('ssoDetail');
        return next;
      },
      { replace: true },
    );
  }, [ssoError, ssoDetail, setSearchParams]);

  useEffect(() => {
    if (entryModule) {
      sessionStorage.setItem(STORAGE_KEYS.PENDING_ENTRY_MODULE, entryModule);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    }
  }, [entryModule]);

  const onSubmit = async (data: FormData) => {
    try {
      if (entryModule) {
        sessionStorage.setItem(STORAGE_KEYS.PENDING_ENTRY_MODULE, entryModule);
      }
      const result = await dispatch(
        login({
          email: data.email,
          password: data.password,
          rememberMe: data.rememberMe,
          entryModule,
          masterLogin: variant === 'master',
        }),
      ).unwrap();
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        notify.info(t('login.mfaPrompt'), { icon: '🔐' });
        return;
      }
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error(t('login.notMasterAdmin'));
        return;
      }
      if (variant === 'organization' && result.masterAdmin) {
        dispatch(clearAuth());
        notify.error(t('login.platformOperatorUrl'));
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      onLoginSuccess(result);
      showLoginSuccessToast(t('login.welcomeBack'), `password:${result.userId ?? data.email}`);
    } catch (err) {
      showApiError(err, t('login.invalidCredentials'));
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || totpCode.length < 6) return;
    try {
      const result = await dispatch(
        verifyMfaLogin({
          mfaToken,
          totpCode,
          entryModule,
          masterLogin: variant === 'master',
        }),
      ).unwrap();
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error(t('login.notMasterAdmin'));
        return;
      }
      if (variant === 'organization' && result.masterAdmin) {
        dispatch(clearAuth());
        notify.error(t('login.platformOperatorUrl'));
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      onLoginSuccess(result);
      showLoginSuccessToast(t('login.welcomeBack'), `mfa:${result.userId ?? mfaToken}`);
    } catch (err) {
      showApiError(err, t('login.invalidMfaCode'));
    }
  };

  if (mfaToken) {
    return (
      <AuthCard title={t('login.mfaTitle')}>
        <form onSubmit={onMfaSubmit} className="space-y-4">
          <AuthInput
            label={t('login.mfaCode')}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <AuthButton type="submit" loading={loading} disabled={totpCode.length < 6}>
            {t('login.mfaVerify')}
          </AuthButton>
          <button
            type="button"
            className="w-full text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={() => {
              setMfaToken(null);
              setTotpCode('');
            }}
          >
            {t('login.backToLogin')}
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <AuthInput label={t('login.email')} type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
        <AuthInput
          label={t('login.password')}
          type="password"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />
        <div className="flex items-center justify-between gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <input type="checkbox" className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" {...register('rememberMe')} />
            {t('login.rememberMe')}
          </label>
          <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
            {t('login.forgotPasswordLink')}
          </Link>
        </div>
        <AuthButton type="submit" loading={loading}>
          {t('login.submit')}
        </AuthButton>
        {variant === 'organization' ? (
          <SsoAuthButtons
            redirectAfter={moduleEntry?.homePath ?? '/dashboard/home'}
            entryModule={entryModule}
            googleLabel={t('login.googleLogin')}
          />
        ) : (
          <SsoAuthButtons redirectAfter="/master" googleLabel={t('login.googleLogin')} showMicrosoft={false} />
        )}
      </form>
      {showRegisterLink && variant === 'organization' ? (
        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('login.newOrganization')}{' '}
          <Link
            to={moduleEntry?.registerPath ?? '/register'}
            className="font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {t('login.createAccount')}
          </Link>
        </p>
      ) : null}
    </AuthCard>
  );
}
